"use client";

import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type PropsWithChildren,
} from "react";

import { useBoundariesEditMode } from "@/app/(internal)/panel/(maps)/_contexts/boundaries-edit-mode-context";
import type { ListboxFieldOption } from "@/components/flowbite/forms/fields/listbox/types";
import { useGetDistrictSchoolsQuery } from "@/providers/store/api/districts/districts";
import type { IDistrictSchoolListItem } from "@/providers/store/api/districts/districts.interface";
import { useAppSelector } from "@/providers/store/hooks";

import {
  buildBoundaryDistrictSchoolsQueryArgs,
  type BoundarySchoolSelectVariant,
} from "./build-boundary-district-schools-query-args";

const EMPTY_SCHOOL_VALUE = "";

interface BoundaryDistrictSchoolsContextValue {
  schools: readonly IDistrictSchoolListItem[];
  isLoading: boolean;
  schoolOptions: ListboxFieldOption[];
}

const BoundaryDistrictSchoolsContext =
  createContext<BoundaryDistrictSchoolsContextValue | null>(null);

export function useBoundaryDistrictSchools(): BoundaryDistrictSchoolsContextValue {
  const context = useContext(BoundaryDistrictSchoolsContext);
  if (!context) {
    throw new Error(
      "useBoundaryDistrictSchools must be used inside <BoundaryDistrictSchoolsProvider />",
    );
  }

  return context;
}

interface BoundaryDistrictSchoolsProviderProps extends PropsWithChildren {
  schoolSelectVariant: BoundarySchoolSelectVariant;
  noSchoolLabel: string;
}

export const BoundaryDistrictSchoolsProvider: FC<BoundaryDistrictSchoolsProviderProps> = ({
  children,
  schoolSelectVariant,
  noSchoolLabel,
}) => {
  const districtId = useAppSelector((s) => s.district.selectedDistrict?.id);
  const { isBoundariesEditMode, editingSchoolType } = useBoundariesEditMode();

  const queryArgs = useMemo(
    () =>
      buildBoundaryDistrictSchoolsQueryArgs({
        districtId: districtId ?? "",
        schoolSelectVariant,
        isBoundariesEditMode,
        editingSchoolType,
      }),
    [districtId, editingSchoolType, isBoundariesEditMode, schoolSelectVariant],
  );

  const { data, isLoading } = useGetDistrictSchoolsQuery(queryArgs, {
    skip: !districtId || !isBoundariesEditMode,
  });

  const schools = useMemo(() => data?.items ?? [], [data?.items]);

  const schoolOptions = useMemo((): ListboxFieldOption[] => {
    return [
      { value: EMPTY_SCHOOL_VALUE, label: noSchoolLabel },
      ...schools.map((school) => ({
        value: school.internalId ?? "",
        label: school.name ?? school.internalId ?? school.id,
      })),
    ];
  }, [noSchoolLabel, schools]);

  const value = useMemo(
    (): BoundaryDistrictSchoolsContextValue => ({
      schools,
      isLoading,
      schoolOptions,
    }),
    [isLoading, schoolOptions, schools],
  );

  return (
    <BoundaryDistrictSchoolsContext.Provider value={value}>
      {children}
    </BoundaryDistrictSchoolsContext.Provider>
  );
};

export type { BoundarySchoolSelectVariant };
